import {
  Module,
  VuexModule,
  Action,
  Mutation,
  getModule
} from 'vuex-module-decorators'
import Vue from 'vue'
import axios from 'axios'

import store from '@/store'
import authModule from '@/store/auth'
import dashboardModule from '@/store/dashboard'
import listModule, { ListType } from '@/store/list'
import whitelabelModule from '@/store/whitelabel'
import { Deal, DealComment, DealStatistics, DealRedemption } from '@/types/Deal'
import { Whitelabel } from '@/types/Whitelabel'
import { isEmpty } from 'lodash'
import { DealType, DealTypeText } from '@/utils/enums'

const name = 'deal'

@Module({
  namespaced: true,
  dynamic: true,
  name,
  store
})
class DealModule extends VuexModule {
  private currentDeal?: Deal
  private currentDealComments: DealComment[] = []
  private currentDealStatistics?: DealStatistics
  private allDeals: Deal[] = []
  private allRedemptions: DealRedemption[] = []

  private get whitelabel(): Whitelabel {
    return whitelabelModule.whitelabel
  }

  get deal(): Deal | undefined {
    return this.currentDeal
  }

  get comments(): DealComment[] {
    return this.currentDealComments || []
  }

  get statistics(): DealStatistics | undefined {
    return this.currentDealStatistics
  }

  get deals(): Deal[] {
    return this.allDeals
  }

  get redemptions(): DealRedemption[] {
    return this.allRedemptions || []
  }

  get dealTypes(): any[] {
    if (authModule.isPartner || authModule.isLightPartner) {
      return Object.entries(DealType)
        .filter(el => el[1] !== DealType.REWARDS)
        .map(entry => [DealTypeText[entry[0]], entry[1]])
    } else {
      return Object.entries(DealType).map(entry => [
        DealTypeText[entry[0]],
        entry[1]
      ])
    }
  }

  @Mutation
  setCurrentDeal(payload: Deal) {
    Vue.set(this, 'currentDeal', payload)
  }

  @Mutation
  setCurrentDealComments(payload: DealComment) {
    Vue.set(this, 'currentDealComments', payload)
  }

  @Mutation
  setCurrentDealStatistics(payload: DealStatistics) {
    Vue.set(this, 'currentDealStatistics', payload)
  }

  @Mutation
  setAllDeals(payload: Deal[]) {
    Vue.set(this, 'allDeals', payload)
  }

  @Mutation
  setAllRedemptions(payload: DealRedemption[]) {
    Vue.set(this, 'allRedemptions', payload)
  }

  @Action({ rawError: true })
  async fetch(id: string) {
    try {
      const { data } = await axios.get(
        `/admin/${this.whitelabel.slug}/deals/${id}`
      )
      if (data.redemptionCodes) {
        Object.assign(data.deal, { redemptionCodes: data.redemptionCodes })
      }
      this.context.commit('setCurrentDeal', data.deal)
      this.context.commit('setCurrentDealComments', data.comments)
    } catch (error) {
      dashboardModule.newError('Deal konnte nicht geladen werden')
    }
  }

  @Action({ rawError: true })
  async fetchStatistics(id: string) {
    try {
      const { data } = await axios.get(
        `/admin/${this.whitelabel.slug}/deals/${id}/statistics`
      )
      this.context.commit('setCurrentDealStatistics', data)
    } catch (error) {
      dashboardModule.newError('Statisiken konnten nicht geladen werden')
    }
  }

  @Action({ rawError: true })
  async fetchAll(params: {
    limit: number
    q?: string
    page?: number
    companyId?: string
    lean?: boolean
    sortBy?: string
    sortDesc?: boolean
    status?: string[]
  }) {
    try {
      if (!isEmpty(listModule.allFilters[ListType.Deal])) {
        Object.assign(params, { filter: listModule.allFilters[ListType.Deal] })
      }

      const { data } = await axios.post(
        `/admin/${this.whitelabel.slug}/deals/list`,
        params
      )
      this.context.commit('setAllDeals', data.deals || data)
      return data.page_count
    } catch (error) {
      dashboardModule.newError('Deals konnten nicht geladen werden')
    }
  }

  @Action({ rawError: true })
  async fetchRedemptions() {
    try {
      const { data } = await axios.get(
        `/admin/${this.whitelabel.slug}/deals/redemptions`
      )
      this.context.commit('setAllRedemptions', data.redemptions)
    } catch (error) {
      dashboardModule.newError('Redemptions konnten nicht geladen werden')
    }
  }

  @Action({ rawError: true })
  async fetchByVenue(params: {
    limit: number
    venueId: string
    q?: string
    page?: number
  }) {
    try {
      const { data } = await axios.get(
        `/admin/${this.whitelabel.slug}/venue/${params.venueId}/deals`,
        {
          params
        }
      )
      this.context.commit('setAllDeals', data.deals)
      return data.page_count
    } catch (error) {
      dashboardModule.newError('Deals konnten nicht geladen werden')
    }
  }

  @Action({ rawError: true })
  async create(payload: {
    whitelabel: string
    company: string
    deal: Partial<Deal>
  }) {
    try {
      const { data } = await axios.post(
        `/admin/${payload.whitelabel}/company/${payload.company}/deals`,
        payload.deal
      )
      this.context.commit('setCurrentDeal', data)
      this.fetchAll({ limit: listModule.perPage })
      dashboardModule.newMessage('Deal wurde erfolgreich erstellt')
    } catch (error) {
      dashboardModule.newError('Deal konnte nicht erstellt werden')
    }
  }

  @Action({ rawError: true })
  async update(payload: { whitelabel: string; deal: Partial<Deal> }) {
    try {
      const { data } = await axios.put(
        `/admin/${payload.whitelabel}/deals/${payload.deal._id}`,
        {
          updatedDeal: payload.deal,
          comment: ''
        }
      )

      this.context.commit('setCurrentDeal', data.deal)
      dashboardModule.newMessage('Deal wurde erfolgreich aktualisiert')
    } catch (error) {
      dashboardModule.newError('Deal konnte nicht aktualisiert werden')
    }
  }

  @Action({ rawError: true })
  async updateRedemption(payload: {
    redemption: DealRedemption
    approved: boolean
  }) {
    try {
      await axios.put(
        `/admin/${payload.redemption.deal.whitelabel}/deals/redemptions/${payload.redemption._id}/review`,
        { approved: payload.approved }
      )
      dashboardModule.newMessage('Redemption wurde erfolgreich aktualisiert')
    } catch (error) {
      dashboardModule.newError('Redemption konnte nicht aktualisiert werden')
    }
  }

  @Action({ rawError: true })
  async addComment(comment: string) {
    try {
      await axios.put(
        `/admin/${this.deal!.whitelabel}/deals/${this.deal!._id}`,
        { comment }
      )

      await this.fetch(this.deal!._id)
      dashboardModule.newMessage('Kommentar wurde erfolgreich erstellt')
    } catch (error) {
      dashboardModule.newError('Kommentar konnte nicht erstellt werden')
    }
  }

  @Action({ rawError: true })
  setDeal(payload: Deal) {
    this.context.commit('setCurrentDeal', payload)
  }
}

export default getModule(DealModule)
